import { apiLocal } from './servers/apiInternal';

export interface AbrangenciaStatusRow {
  search: string;
  platform: string;
  query: string;
  key: string;
}

export interface StatusAbrangenciaAtivaRow extends AbrangenciaStatusRow {
  value: string | null;
}

export interface StatusAbrangenciasAtivasResponse {
  success: boolean;
  total: number;
  data: StatusAbrangenciaAtivaRow[];
  message?: string;
  error?: string;
}

export interface ErrosConsecutivosRow extends AbrangenciaStatusRow {
  value?: string | null;
  tempo_em_erro?: string | {
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
    milliseconds?: number;
  };
  total_erros?: number;
  erro_desde?: string;
  ultimo_erro?: string;
}

export interface ErrosDiaAnteriorRow extends AbrangenciaStatusRow {
  total: number;
}

export interface DemorandoRetornoRow extends AbrangenciaStatusRow {
  total: number;
  status?: string;
  is_pending?: boolean;
}

export interface StatusSummaryMetric {
  total: number;
  percentage: number;
}

export interface StatusDashboardStatusSummary {
  total: number;
  success: StatusSummaryMetric;
  warning: StatusSummaryMetric;
  error: StatusSummaryMetric;
  pending: StatusSummaryMetric;
}

export interface StatusDashboardTopErrorRow {
  label: string;
  total: number;
  percentage: number;
}

export interface StatusDashboardByKeyRow {
  key: string;
  success: number;
  warning: number;
  error: number;
  pending: number;
  total: number;
}

export interface TribunalHealthByKey {
  cnj: number;
  doc: number;
  nomeparte: number;
  noeadv: number;
  oab: number;
}

export interface TribunalHealthMetric extends StatusSummaryMetric {
  byKey: TribunalHealthByKey;
}

export interface StatusDashboardTribunalHealth {
  totalTribunais: number;
  operacional: TribunalHealthMetric;
  parcial: TribunalHealthMetric;
  indisponivel: TribunalHealthMetric;
}

export interface StatusDashboardOverviewResponse {
  success: boolean;
  statusSummary: StatusDashboardStatusSummary;
  topErrors: {
    abrangencias: StatusDashboardTopErrorRow[];
    searchPlataformas?: StatusDashboardTopErrorRow[];
    tribunais?: StatusDashboardTopErrorRow[];
    plataformas?: StatusDashboardTopErrorRow[];
  };
  byKey: StatusDashboardByKeyRow[];
  tribunalHealth: StatusDashboardTribunalHealth;
  fromCache?: boolean;
  cachedAt?: string;
  message?: string;
  error?: string;
}

export interface PaginatedStatusResponse<T> {
  success: boolean;
  page: number;
  pageSize: number;
  total: number;
  data: T[];
  availableKeys?: string[];
  fromCache?: boolean;
  cachedAt?: string;
  message?: string;
  error?: string;
}

export interface ErrosConsecutivosResponse extends PaginatedStatusResponse<ErrosConsecutivosRow> {
  selectSql?: string;
  selectParams?: {
    key: string;
    pageSize: number;
    offset: number;
  };
}

export interface DemorandoRetornoResponse extends PaginatedStatusResponse<DemorandoRetornoRow> {
  period: 'previous' | 'current';
}

export interface ApiConsultaErroRow {
  platform: string;
  search: string;
  query: string;
  param: string;
  value: string;
  total: number;
}

export interface ApiConsultaErrosResponse {
  success: boolean;
  days: 3 | 7;
  statusSummary: StatusDashboardStatusSummary;
  topErrors: {
    abrangencias: StatusDashboardTopErrorRow[];
    searchPlataformas: StatusDashboardTopErrorRow[];
    tribunais: StatusDashboardTopErrorRow[];
    plataformas: StatusDashboardTopErrorRow[];
  };
  data: ApiConsultaErroRow[];
  fromCache?: boolean;
  cachedAt?: string;
  cachePending?: boolean;
  cacheIncomplete?: boolean;
  message?: string;
  error?: string;
}

let overviewRequestInFlight: Promise<StatusDashboardOverviewResponse> | null = null;
const apiConsultaRequestsInFlight = new Map<string, Promise<ApiConsultaErrosResponse>>();

export async function resetStatusDashboardTablesCache(): Promise<void> {
  await apiLocal.post('/status/dashboard/reset-tabelas-cache');
}

export async function fetchStatusDashboardOverview(): Promise<StatusDashboardOverviewResponse> {
  if (overviewRequestInFlight) {
    return overviewRequestInFlight;
  }

  overviewRequestInFlight = apiLocal
    .get('/status/dashboard/resumo')
    .then((resp) => resp.data)
    .finally(() => {
      overviewRequestInFlight = null;
    });

  return overviewRequestInFlight;
}

export async function fetchErrosConsecutivos(params: {
  key?: string;
  page?: number;
  pageSize?: number;
  forceRefresh?: boolean;
}): Promise<ErrosConsecutivosResponse> {
  const { forceRefresh, ...query } = params;
  const resp = await apiLocal.get('/status/dashboard/erros-consecutivos', {
    params: {
      ...query,
      ...(forceRefresh ? { forceRefresh: 'true' } : {}),
    },
  });
  return resp.data;
}

export async function fetchErrosDiaAnterior(params: {
  comparison?: 'gte' | 'lt';
  threshold?: number;
  key?: string;
  page?: number;
  pageSize?: number;
  forceRefresh?: boolean;
}): Promise<PaginatedStatusResponse<ErrosDiaAnteriorRow>> {
  const { forceRefresh, ...query } = params;
  const resp = await apiLocal.get('/status/dashboard/erros-dia-anterior', {
    params: {
      ...query,
      ...(forceRefresh ? { forceRefresh: 'true' } : {}),
    },
  });
  return resp.data;
}

export async function fetchDemorandoRetorno(params: {
  period?: 'previous' | 'current';
  key?: string;
  page?: number;
  pageSize?: number;
  forceRefresh?: boolean;
}): Promise<DemorandoRetornoResponse> {
  const { forceRefresh, ...query } = params;
  const resp = await apiLocal.get('/status/dashboard/demorando-retorno', {
    params: {
      ...query,
      ...(forceRefresh ? { forceRefresh: 'true' } : {}),
    },
  });
  return resp.data;
}

export type ApiConsultaCachePart = 'summary' | 'topErrors' | 'cnj';

/** Cobre lock Redis (até 180s) + query CW (até 120s) + margem. */
const API_CONSULTA_FORCE_REFRESH_TIMEOUT_MS = 190_000;
/** Leituras com warm-up em background — polling curto até o cache popular. */
const API_CONSULTA_READ_TIMEOUT_MS = 30_000;

export async function fetchApiConsultaErros(params: {
  days?: 3 | 7;
  forceRefresh?: boolean;
  part?: ApiConsultaCachePart;
}): Promise<ApiConsultaErrosResponse> {
  const days = params.days ?? 3;
  const forceRefresh = params.forceRefresh === true;
  const part = params.part;
  const baseKey = `${days}:${part ?? 'all'}`;
  const inFlightKey = forceRefresh ? `${baseKey}:force` : `${baseKey}:read`;

  if (forceRefresh) {
    apiConsultaRequestsInFlight.delete(`${baseKey}:read`);
  }

  const inFlight = apiConsultaRequestsInFlight.get(inFlightKey);
  if (inFlight) {
    return inFlight;
  }

  const request = apiLocal
    .get('/status/dashboard/api-consulta-erros', {
      params: {
        days,
        ...(part ? { part } : {}),
        ...(forceRefresh ? { forceRefresh: 'true' } : {}),
      },
      timeout: forceRefresh ? API_CONSULTA_FORCE_REFRESH_TIMEOUT_MS : API_CONSULTA_READ_TIMEOUT_MS,
    })
    .then((resp) => resp.data as ApiConsultaErrosResponse)
    .finally(() => {
      apiConsultaRequestsInFlight.delete(inFlightKey);
    });

  apiConsultaRequestsInFlight.set(inFlightKey, request);
  return request;
}

export async function fetchStatusAbrangenciasAtivas(): Promise<StatusAbrangenciasAtivasResponse> {
  const resp = await apiLocal.get('/status/dashboard/abrangencias-ativas');
  return resp.data;
}

export interface AbrangenciaSyncCheckResponse {
  success: boolean;
  productionTotal: number;
  statusTotal: number;
  missingInStatus: AbrangenciaStatusRow[];
  extraInStatus: AbrangenciaStatusRow[];
  notDisplayed: AbrangenciaStatusRow[];
  showOutdatedAlert: boolean;
  showNotDisplayedAlert: boolean;
  message?: string;
  error?: string;
}

export async function fetchAbrangenciasSyncCheck(): Promise<AbrangenciaSyncCheckResponse> {
  const resp = await apiLocal.get('/status/dashboard/abrangencias-sync-check');
  return resp.data;
}
